import {createSlice} from '@reduxjs/toolkit';
import {
  fetchCodeForSite,
  fetchMigrationStatus,
  fetchSiteConfiguration,
  startMigration,
  updateSiteConfiguration,
  fetchEmojiCategories,
  updateSiteEmojiCategories,
  fetchSiteEmojiConfiguration,
  fetchArticleSentimentAccess,
  fetchAccountProducts,
  sendArticleSentimentAccessRequest,
  fetchTrendingNowAccess,
  sendTrendingNowAccessRequest,
  fetchTrendingNowConfiguration,
  updateTrendingNowConfiguration,
  fetchTrendingNowCodeForSite
} from "../thunks/engagement"

export const DEFAULT_MIGRATION_STATUS = null //READY/STARTED/COMPLETED/NOT_REQUIRED
export const MIGRATION_ERROR = 'MIGRATION_ERROR'
export const MIGRATION_READY = 'READY'
export const MIGRATION_STARTED = 'STARTED'
export const MIGRATION_COMPLETED = 'COMPLETED'
export const MIGRATION_NOT_REQUIRED = 'NOT_REQUIRED'
export const PRODUCT_REQUEST_PENDING = 'PENDING';

export const DEFAULT_TITLE = "What’s your reaction to this article?";

export const DEFAULT_TRENDING_TITLE = "Trending Now";
const DEFAULT_TIMEFRAME = 'DAY'
const DEFAULT_DISPLAY = 'FLAT'
const DEFAULT_VISIBLE_AMOUNT = 10
const DEFAULT_METHOD_TO_SELECT = 'VIEWS_COUNT'
const DEFAULT_IMAGE_VALUE = 'STANDARD'

export const TIME_RANGE_LIST = [
  {title: '24 Hours', value: 'DAY'},
  {title: '7 Days', value: 'WEEK'},
  {title: '30 Days', value: 'MONTH'},
];

export const METHOD_TO_SELECT_LIST = [
  {title: 'Engagement', value: 'REACTIONS_COUNT', order: '0'},
  {title: 'Most Popular', value: 'VIEWS_COUNT', order: '1'}
];

export const ARTICLE_DISPLAY_LIST = [
  {title: 'Flat', value: 'FLAT', order: '0'},
  {title: 'Scroll', value: 'SCROLL', order: '1'},
  {title: 'Rail Scroll', value: 'RAIL_SCROLL', order: '2'},
  {title: 'Scroll with ad', value: 'SCROLL_WITH_AD', order: '3'},
];

export const FLAT_ARTICLES_AMOUNT_LIST = [
  {title: '2', value: 2},
  {title: '4', value: 4},
  {title: '6', value: 6},
  {title: '10', value: 10},
];

export const RAIL_ARTICLES_AMOUNT_LIST = [
  {title: '1', value: 1},
  {title: '2', value: 2},
  {title: '3', value: 3},
];

export const PLACEHOLDER_IMAGES_LIST = [
  {title: 'Standard', value: 'STANDARD'},
  {title: 'Custom', value: 'CUSTOM'},
];

export const DEFAULT_COLORS = {
  background: "#FFFFFF",
  primaryText: "#062B75",
  secondaryText: "#ABB2C3",
  branding: "#1494F4",
}

const initialState = {
  accountProducts: [],
  articleSentimentAccessRequest: {},
  trendingNowAccessRequest: {},
  migrationStatus: DEFAULT_MIGRATION_STATUS,
  isLoading: false,
  articleSentimentUnitUUID: null,
  configurationUUID: null,
  title: DEFAULT_TITLE,
  enableOverview: false,
  overviewTimeRange: DEFAULT_TIMEFRAME,
  enablePageId: false,
  colorConfiguration: DEFAULT_COLORS,
  headerCode: '',
  articleSentimentUnitCode: '',
  emojiConfigurations: {},
  emojisCategories: [],
  // Trending now
  titleTrending: DEFAULT_TRENDING_TITLE,
  configurationUUIDTrending: null,
  selection_method: DEFAULT_METHOD_TO_SELECT,
  display: DEFAULT_DISPLAY,
  visible_amount: DEFAULT_VISIBLE_AMOUNT,
  selected_timeframe: DEFAULT_TIMEFRAME,
  isCustomImage: DEFAULT_IMAGE_VALUE,
  placeholder_img_url: null,
  commenting_enabled: false,
  sentiment_enabled: false,
  trendingNowHeaderCode: '',
  trendingNowUnitCode: '',
  trendingNowUnitUUID: null
}

export const engagementSlice = createSlice({
  name: 'engagement',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // fetchAccountProducts
      .addCase(fetchAccountProducts.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchAccountProducts.fulfilled, (state, action) => {
        state.accountProducts = action.payload;
        state.isLoading = false
      })
      .addCase(fetchAccountProducts.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // fetchArticleSentimentAccess
      .addCase(fetchArticleSentimentAccess.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchArticleSentimentAccess.fulfilled, (state, action) => {
        state.articleSentimentAccessRequest = action.payload;
        state.isLoading = false
      })
      .addCase(fetchArticleSentimentAccess.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // sendArticleSentimentAccessRequest
      .addCase(sendArticleSentimentAccessRequest.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(sendArticleSentimentAccessRequest.fulfilled, (state, action) => {
        state.articleSentimentAccessRequest = { requestStatus: PRODUCT_REQUEST_PENDING }
        state.isLoading = false
      })
      .addCase(sendArticleSentimentAccessRequest.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // fetchTrendingNowAccess
      .addCase(fetchTrendingNowAccess.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchTrendingNowAccess.fulfilled, (state, action) => {
        state.trendingNowAccessRequest = action.payload;
        state.isLoading = false
      })
      .addCase(fetchTrendingNowAccess.rejected, (state, action) => {
        state.isLoading = false
      })

      // sendTrendingNowAccessRequest
      .addCase(sendTrendingNowAccessRequest.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(sendTrendingNowAccessRequest.fulfilled, (state, action) => {
        state.trendingNowAccessRequest = { requestStatus: PRODUCT_REQUEST_PENDING }
        state.isLoading = false
      })
      .addCase(sendTrendingNowAccessRequest.rejected, (state, action) => {
        state.isLoading = false
      })

      // fetchTrendingNowConfiguration
      .addCase(fetchTrendingNowConfiguration.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchTrendingNowConfiguration.fulfilled, (state, action) => {
        const {
          uuid,
          title,
          display,
          placeholder_img_url,
          visible_amount,
          selected_timeframe,
          selection_method,
          trendingNowUnitUUID
        } = action.payload;

        state.configurationUUIDTrending = uuid
        state.titleTrending = title
        state.display = display
        state.placeholder_img_url = placeholder_img_url
        state.isCustomImage = placeholder_img_url ? 'CUSTOM' : 'STANDARD'
        state.visible_amount = visible_amount
        state.selected_timeframe = selected_timeframe
        state.selection_method = selection_method
        state.isLoading = false
        state.trendingNowUnitUUID = trendingNowUnitUUID
      })
      .addCase(fetchTrendingNowConfiguration.rejected, (state, action) => {
        state.isLoading = false
      })

    // fetchTrendingNowCodeForSite
    .addCase(fetchTrendingNowCodeForSite.pending, (state, action) => {
      state.isLoading = true
    })
    .addCase(fetchTrendingNowCodeForSite.fulfilled, (state, action) => {
      const {
        headerCode,
        trendingNowUnitCode
      } = action.payload
      state.trendingNowHeaderCode = headerCode;
      state.trendingNowUnitCode = trendingNowUnitCode;
      state.isLoading = false
    })
    .addCase(fetchTrendingNowCodeForSite.rejected, (state, action) => {
      state.isLoading = false
    })

      // updateTrendingNowConfiguration
      .addCase(updateTrendingNowConfiguration.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(updateTrendingNowConfiguration.fulfilled, (state, action) => {
        const {
          uuid,
          title,
          display,
          placeholder_img_url,
          visible_amount,
          selected_timeframe,
          selection_method,
          commenting_enabled,
          sentiment_enabled,
          trendingNowUnitUUID
        } = action.payload;

        state.configurationUUIDTrending = uuid
        state.titleTrending = title
        state.display = display
        state.placeholder_img_url = placeholder_img_url
        state.isCustomImage = placeholder_img_url ? 'CUSTOM' : 'STANDARD'
        state.visible_amount = visible_amount
        state.selected_timeframe = selected_timeframe
        state.selection_method = selection_method
        state.isLoading = false
        state.commenting_enabled = commenting_enabled
        state.sentiment_enabled = sentiment_enabled
        state.trendingNowUnitUUID = trendingNowUnitUUID
      })
      .addCase(updateTrendingNowConfiguration.rejected, (state, action) => {
        state.isLoading = false
      })

      // fetchCodeForSite
      .addCase(fetchCodeForSite.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchCodeForSite.fulfilled, (state, action) => {
        const {
          headerCode,
          articleSentimentUnitCode
        } = action.payload
        state.headerCode = headerCode;
        state.articleSentimentUnitCode = articleSentimentUnitCode;
        state.isLoading = false
      })
      .addCase(fetchCodeForSite.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // fetchSiteConfiguration
      .addCase(fetchSiteConfiguration.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchSiteConfiguration.fulfilled, (state, action) => {
        const {
          articleSentimentUnitUUID,
          configurationUUID,
          title,
          enableOverview,
          overviewTimeRange,
          enablePageId,
          colorConfiguration,
        } = action.payload;

        state.articleSentimentUnitUUID = articleSentimentUnitUUID
        state.configurationUUID = configurationUUID
        state.title = title
        state.enableOverview = enableOverview
        state.overviewTimeRange = overviewTimeRange
        state.enablePageId = enablePageId
        state.colorConfiguration = colorConfiguration
        state.isLoading = false
      })
      .addCase(fetchSiteConfiguration.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // updateSiteConfiguration
      .addCase(updateSiteConfiguration.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(updateSiteConfiguration.fulfilled, (state, action) => {
        const {
          configurationUUID,
          title,
          enableOverview,
          overviewTimeRange,
          enablePageId,
          colorConfiguration,
        } = action.payload;

        state.configurationUUID = configurationUUID
        state.title = title
        state.enableOverview = enableOverview
        state.overviewTimeRange = overviewTimeRange
        state.enablePageId = enablePageId
        state.colorConfiguration = colorConfiguration
        state.isLoading = false
      })
      .addCase(updateSiteConfiguration.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // fetchMigrationStatus
      .addCase(fetchMigrationStatus.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchMigrationStatus.fulfilled, (state, action) => {
        state.migrationStatus = action.payload
        state.isLoading = false
      })
      .addCase(fetchMigrationStatus.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // startMigration
      .addCase(startMigration.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(startMigration.fulfilled, (state, action) => {
        //console.log(action)
        state.migrationStatus = MIGRATION_STARTED
        state.isLoading = false
      })
      .addCase(startMigration.rejected, (state, action) => {
        //console.log(action)
        state.migrationStatus = MIGRATION_ERROR
        state.isLoading = false
      })

      // fetchSiteEmojiConfiguration
      .addCase(fetchSiteEmojiConfiguration.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchSiteEmojiConfiguration.fulfilled, (state, action) => {
        //console.log(action)
        state.emojiConfigurations = action.payload
        state.isLoading = false
      })
      .addCase(fetchSiteEmojiConfiguration.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // fetchEmojiCategories
      .addCase(fetchEmojiCategories.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(fetchEmojiCategories.fulfilled, (state, action) => {
        //console.log(action)
        state.emojisCategories = action.payload
        state.isLoading = false
      })
      .addCase(fetchEmojiCategories.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })

      // updateSiteEmojiCategories
      .addCase(updateSiteEmojiCategories.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(updateSiteEmojiCategories.fulfilled, (state, action) => {
        //console.log(action)
        const {
          siteUUID,
          emojis,
        } = action.payload;

        const newEmojis = state?.emojiConfigurations?.emojis?.map(e => {
          const emoji = emojis.find(emoji => e.emojiUUID === emoji.emojiUUID)
          const {
            active,
            emojiCategoryUUID,
            emojiUUID,
            lastUpdatedAt,
          } = emoji || {};

          const emojiCategory = state?.emojisCategories?.find(c =>
            c.emojiCategoryUUID === emojiCategoryUUID
          );

          return emoji
            ? {
              emojiUUID,
              emojiCategory,
              active,
              lastUpdatedAt,
            }
            : e
        }) || [];

        state.emojiConfigurations.emojis = newEmojis
        state.emojiConfigurations.siteUUID = siteUUID
        state.isLoading = false
      })
      .addCase(updateSiteEmojiCategories.rejected, (state, action) => {
        //console.log(action)
        state.isLoading = false
      })
  }
});

export default engagementSlice.reducer;
